import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import ModuleInterest from '../../components/common/ModuleInterest';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import NotificationBell from '../../components/common/NotificationBell';
import ProfileCompleteness from '../../components/common/ProfileCompleteness';
import AnnouncementModal from '../../components/common/AnnouncementModal';
import StatTile from '../../components/common/StatTile';
import { ManagingBar } from './ClubPage';
import PulseStrip from '../../components/common/PulseStrip';
import SdgIcon from '../../components/common/SdgIcon';
import MyBreakout from '../../components/common/MyBreakout';
import MeetQrSheet from '../../components/common/MeetQrSheet';
import LabToday from './LabToday';
import useResetSubmittingOnRestore from '../../hooks/useResetSubmittingOnRestore';
import HouseBars, { HouseSwatch } from '../../components/common/HouseBars';
import { purposeLabel } from '../../components/common/EventTags';
import { apiErrorText, apiRequest, formatDate, eventDayLabel, eventStartLabel,
  groupSponsorsByType, teamPhoto } from '../../utils';
import './MemberHome.css';

// The keys token_required answers a request without a usable session with (api/services.py).
const NO_SESSION = ['missingToken', 'sessionExpired', 'invalidToken', 'userNotFound'];

// GUIDs come back upper-case from the database and may be lower-case off a token.
const sameId = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

/* Bravos MEMBER home — one page, two role-driven views (GET /api/bravos/home tells us
   which via `view`):
     - 'member'  (individual member / employee / a contact person acting as a member):
                 what their company stands for + personal assessment CTAs + the BU
                 calendar (with a "Register" affordance on Labs they're not in yet) + the
                 content feed.
     - 'contact' (a company's contact person, the admin view): a GRID OF TILES — journey,
                 pulses, assessments, tickets, people, company signals, and the feed and the
                 photo wall too — each a number and a screen behind it, then the calendar.
                 The full-width cards under the calendar are the member view's; an admin
                 reads those same two counts off the grid.

   ★ Why the contact view is tiles and the member view is not. The admin has six standing
   jobs and comes back to check on them; a number per job with one tap to the detail is the
   shape that serves that, and it is the same widget a Hercules Trophy captain reads their
   readiness from. A member has ONE job — do the pulse in front of them — so putting it
   behind a tile would add a tap to the only thing they came for.

   The user flips between the two with the existing role picker (/role). Reached via
   GET /api/home ({redirect:'/member-home'}) for an active member of a BRAVOS BU. */

const fallbackEvent = require('./../../assets/images/fallback/event.png');
const fallbackCompany = require('./../../assets/images/fallback/company.png');

// How many Labs the calendar shows per direction before "Show more" takes over. The same
// number both ways, so the two halves of the card read as one list rather than two settings.
const CALENDAR_ROWS = 3;

// How many teams the competition rail shows before /my-competition takes over. Four, because
// the rail scrolls and a fifth tile a reader has to swipe for is not a glance any more.
const MYCOMP_TEAMS = 4;

// The BU's public social pages — a small icon strip on the brand card (all Bravos homes).
const SOCIAL_ICON = {
  instagram: { name: 'instagram', pack: 'brands', color: '#C13584' },
  facebook: { name: 'facebook', pack: 'brands', color: '#1877F2' },
  linkedin: { name: 'linkedin', pack: 'brands', color: '#0A66C2' },
  youtube: { name: 'youtube', pack: 'brands', color: '#FF0000' },
  website: { name: 'globe', pack: 'br', color: 'inherit' },
};
const SOCIAL_ORDER = ['instagram', 'facebook', 'linkedin', 'youtube', 'website'];

const SocialLinks = ({ links }) => {
  const items = SOCIAL_ORDER.filter((k) => links && links[k]);
  if (!items.length) return null;
  return (
    <div className="bravos-social">
      {items.map((k) => {
        const cfg = SOCIAL_ICON[k];
        return (
          <a key={k} href={links[k]} target="_blank" rel="noreferrer"
             className="bravos-social__link" aria-label={k}>
            <Icon name={cfg.name} pack={cfg.pack} style={{ color: cfg.color }} />
          </a>
        );
      })}
    </div>
  );
};

const SOCIAL_LABEL = { instagram: 'Instagram', facebook: 'Facebook', youtube: 'YouTube', website: 'Website', linkedin: 'LinkedIn' };
const VIEW_ORDER = ['instagram', 'facebook', 'linkedin', 'youtube', 'website'];
const fmtNum = (n) => Number(n || 0).toLocaleString();

// Ecosystem "Community" card — members vs reach + social views over the last 3 logged
// months (LinkedIn counts impressions), with links out.
// Self-fetches (the reach counts are heavy, so they load lazily after the home renders).
const CommunityStats = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    apiRequest('GET', '/bravos/community-stats').then(setStats).catch(() => {});
  }, []);
  if (!stats) return null;
  const links = stats.social_links || {};
  const views = stats.views_last_3_months || {};
  const viewItems = VIEW_ORDER.filter((k) => views[k] != null);
  return (
    <Card>
      <Title my="1" className="mt-2">
        <Icon name="chart-histogram" theme="primary" className="me-2" />
        {t('communityStats')}
      </Title>
      <div className="bravos-stats">
        <div className="bravos-stat">
          <span className="bravos-stat__num">{fmtNum(stats.community_participants)}</span>
          <span className="bravos-stat__label">{t('communityParticipants')}</span>
          <span className="bravos-stat__sub">{t('ofReached', { n: fmtNum(stats.people_reached) })}</span>
        </div>
        <div className="bravos-stat">
          <span className="bravos-stat__num">{fmtNum(stats.member_companies)}</span>
          <span className="bravos-stat__label">{t('memberCompanies')}</span>
          <span className="bravos-stat__sub">{t('ofReached', { n: fmtNum(stats.companies_reached) })}</span>
        </div>
      </div>
      {viewItems.length > 0 && (
        <>
          <p className="bravos-cal__label">{t('viewsLast3Months')}</p>
          <div className="bravos-stats">
            {viewItems.map((k) => {
              const cfg = SOCIAL_ICON[k];
              const inner = (
                <>
                  <span className="bravos-stat__num">{fmtNum(views[k])}</span>
                  <span className="bravos-stat__label">
                    <Icon name={cfg.name} pack={cfg.pack} style={{ color: cfg.color }} className="me-1" />
                    {SOCIAL_LABEL[k]}
                  </span>
                </>
              );
              return links && links[k] ? (
                <a key={k} className="bravos-stat bravos-stat--link" href={links[k]}
                   target="_blank" rel="noreferrer">{inner}</a>
              ) : (
                <div key={k} className="bravos-stat">{inner}</div>
              );
            })}
          </div>
        </>
      )}
    </Card>
  );
};

// Blob URLs can carry spaces, and an unquoted CSS url() with a space silently loads nothing —
// the same encode the participant home's tiles do.
const cssUrl = (image) => (
  typeof image === 'string' && image.includes(' ') ? encodeURI(image) : image);

/* "How are we doing?" — the competition their EMPLOYER runs, for somebody who runs none of it.
   The leagues card one row down answers "what am I in this season"; this one answers the
   question you ask on the way back from the evening.

   ★ Self-fetching, exactly like CommunityStats above and for the same reason: the house standing
   is `company_house_points` on the server, which visits up to 24 days of five-way joins. Folding
   it into GET /api/bravos/home would make every member wait for it, including the ones whose
   company has no houses at all.

   ★ Draws NOTHING when there is nothing to say — no houses, no teams. A member of a company that
   never ran a competition gets the home they had before, not an empty trophy card.

   ★ Three things and a chevron, in the order somebody actually asks them: which house am I,
   where does it stand, which teams am I in. Everything else — the full standing, the days, the
   boards — is behind /my-competition. */
const MyCompetition = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    // Silent on failure: this card must never be the reason a home page shows an error.
    apiRequest('GET', '/company/my-competition').then(setData).catch(() => {});
  }, []);

  if (!data) return null;
  const houses = data.houses || [];
  const teams = data.teams || [];
  const mine = data.house;
  if (houses.length === 0 && teams.length === 0) return null;

  return (
    <Card>
      <Title my="1" className="mt-2">
        <Icon name="trophy" theme="primary" className="me-2" />
        {t('myCompetition')}
      </Title>

      {/* Which house they are OF — their standing membership, not whichever house their team
          happened to compete for on a given day. The two diverge normally (the shuffle packs
          teams by company), and the day's own board is where that is said per day. */}
      {mine && (
        <button type="button" className="mycomp-me" onClick={() => navigate('/my-competition')}>
          <HouseSwatch house={mine} size="lg" />
          <span className="mycomp-me__body">
            <span className="mycomp-me__label">{t('myHouse')}</span>
            <span className="mycomp-me__name">{mine.name}</span>
            <span className="mycomp-me__sub">
              {t('housePlaceOf', { rank: mine.rank, total: houses.length })}
              {' · '}
              {t('nPoints', { n: Number(mine.points || 0).toLocaleString() })}
            </span>
          </span>
          <Icon name="angle-right" className="bravos-event__chev" />
        </button>
      )}

      {houses.length > 0 && (
        <HouseBars rows={houses} mineId={mine && mine.id} />
      )}

      {/* The teams they play in, newest first — a rail rather than a list, because this is a
          glance and the screen behind it is the list. */}
      {teams.length > 0 && (
        <>
          <p className="bravos-cal__label">{t('myTeams')}</p>
          <div className="mycomp-rail">
            {teams.slice(0, MYCOMP_TEAMS).map((team) => (
              <button
                key={team.team_id}
                type="button"
                className="mycomp-team"
                onClick={() => navigate(`/public-team/${team.team_id}`)}
              >
                <span
                  className="mycomp-team__media"
                  style={{ backgroundImage: `url("${cssUrl(teamPhoto(team.photo_url, team.company_picture))}")` }}
                >
                  {/* Absent while the board is closed: nothing is revealed, so every team is
                      level and a "#2" would be the tie-break talking, not a position. */}
                  {team.rank != null && (
                    <span className="mycomp-team__rank">#{team.rank}</span>
                  )}
                </span>
                <span className="mycomp-team__name">{team.name}</span>
                <span className="mycomp-team__sub">
                  {team.board_state === 'closed'
                    ? t('boardClosedShort')
                    : t('nPoints', { n: Number(team.points || 0).toLocaleString() })}
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      <button type="button" className="bravos-memberslink"
              onClick={() => navigate('/my-competition')}>
        <span className="bravos-memberslink__body">
          <span className="bravos-memberslink__title">{t('seeTheCompetition')}</span>
          <span className="bravos-memberslink__count">{t('seeTheCompetitionHint')}</span>
        </span>
        <Icon name="angle-right" className="bravos-event__chev" />
      </button>
    </Card>
  );
};

// How many chips the badge draws before it stops and counts the rest. A company may declare
// forty values; the badge is one strip and its job is recognition, not the full list, which is
// one tap away on /programmes.
const PURPOSE_SHOWN = 6;

/* What the company says it stands for, under the line: a value is its own picture and its own
   word, a goal is the official UN tile.

   ★ Every chip is a LINK into that one thing — /purpose/value/<id>, /purpose/sdg/<number> —
   where the picture is full size and whatever the company wrote under it is readable. A chip is
   the shortest possible summary of something a company thought about; leaving it as decoration
   inside somebody else's link was the wrong end of that. The page is open to an employee too,
   without the edit button. */
const PurposeStrip = ({ values, sdgs, houses }) => {
  const { t } = useTranslation();
  const vals = values || [];
  const goals = sdgs || [];
  const homes = houses || [];
  if (vals.length === 0 && goals.length === 0 && homes.length === 0) return null;
  const moreValues = vals.length - PURPOSE_SHOWN;
  const moreGoals = goals.length - PURPOSE_SHOWN;
  return (
    <div className="jhead__purpose">
      {vals.slice(0, PURPOSE_SHOWN).map((v) => (
        <Link key={v.id} to={`/purpose/value/${v.id}`} className="jhead__value">
          {/* A value without a picture keeps the circle, empty: the row stays a row of the same
              chips rather than two shapes depending on who uploaded what. */}
          <span
            className="jhead__value-img"
            style={v.image ? { backgroundImage: `url("${cssUrl(v.image)}")` } : undefined}
          />
          {v.name}
        </Link>
      ))}
      {moreValues > 0 && (
        <Link to="/values" className="jhead__value jhead__value--more">
          {`+${moreValues}`}
        </Link>
      )}
      {/* The goals carry no label beside them: the official tile holds its number and its own
          words, which is exactly how every sustainability report on earth prints them. The
          translated name is the alt text, so a screen reader still says it. */}
      {goals.slice(0, PURPOSE_SHOWN).map((g) => (
        <Link key={g.number} to={`/purpose/sdg/${g.number}`} className="jhead__sdglink"
              aria-label={purposeLabel(t, g)}>
          <SdgIcon number={g.number} className="jhead__sdg" alt={purposeLabel(t, g)} />
        </Link>
      ))}
      {moreGoals > 0 && (
        <Link to="/sdgs" className="jhead__value jhead__value--more">
          {`+${moreGoals}`}
        </Link>
      )}
      {/* The houses the company divides itself into — the third thing it says about itself, and
          the only one a colleague actually BELONGS to. Their pictures are why they earn a place
          on this strip; a name and a colour is what it already had room for.

          ★ Not links, unlike the values and the goals beside them. Those two open a page that is
          open to an employee; a house has no such page — /programmes is the admin's screen and
          would bounce everybody else to the home they are already on. A chip that goes nowhere
          is better than a chip that goes nowhere USEFUL.

          ★ Every house, not the first six. A company runs four, or six; the cap the values need
          (a company may declare forty) would only ever fire on a shelf that is already wrong. */}
      {homes.map((h) => (
        <span key={h.id} className="jhead__value jhead__value--house">
          {/* backgroundColor + backgroundImage, not the shorthand — see the Houses tab. */}
          <span
            className="jhead__value-img jhead__house-img"
            style={{ backgroundColor: h.color || '#5a6472',
                     backgroundImage: h.image ? `url("${cssUrl(h.image)}")` : undefined }}
          />
          {h.name}
        </span>
      ))}
    </div>
  );
};

/* The company in one badge, at the top of BOTH homes: what it calls its journey, the programme
   it is in, and what it says it stands for.

   ★★ **It does not name a theme any more.** It used to read "Culture year 2027: Wellbeing" —
   the programme and the one theme running inside it. Themes may overlap since 2026-09-13, so
   there is no such thing as "the" running theme: a company runs a wellbeing theme all quarter
   with a recognition push inside it, and a badge with room for one name would quietly hide the
   rest. The whole list lives on /themes (and on /journey, with the windows), which is where a
   theme is written and where several of them can be read as several. The PROGRAMME stays —
   programmes still never overlap, so naming one is always honest.

   ★ Two shapes, one payload. `tile` is the admin's, across the top of the tile grid; `card` is
   the employee's home, where there is no grid and nothing to edit — so it drops the percentage
   and is not a link at all.

   ★ The admin's tile is NOT one big link any more, and that is the point of it: the name and
   the programme go to /programmes, which is where they are written, the readiness percentage
   goes to /journey, which is what the percentage measures, and every chip goes to its own
   thing. One anchor over all of it meant three different questions landed on one answer — and
   a link inside a link is not markup a browser will keep.

   Renders nothing when the company has filled in none of it (the API then sends no head at
   all), which is what puts the plain percentage tile back on the admin's grid. */
/* A few pictures under a way in — the same strip on three cards: the stream's newest, a shuffle
   of the missions that were said yes to, and a shuffle of the wall.

   ★ Decoration in the strict sense. The whole strip is ONE button to the same place the line
   above it goes, so nothing here is a second destination; and when the server sends no pictures
   — an older payload, a slow read, a company that has taken none — the card falls back to
   exactly the line it always was. A count is a fact and a photograph is an invitation, which is
   the only reason any of the three exist.

   Each tile's alt text is whatever makes THAT picture legible: the mission it answers where
   there is one, otherwise the evening it was taken on. */
const PhotoStrip = ({ items, label, onOpen }) => (
  (items || []).length > 0 ? (
    <button type="button" className="bravos-photostrip" onClick={onOpen} aria-label={label}>
      {items.map((p) => (
        <span className="bravos-photostrip__tile" key={p.id}>
          <img src={p.url} alt={p.label || p.event_name || ''} loading="lazy" />
        </span>
      ))}
    </button>
  ) : null
);

const JourneyHead = ({ head, pct, variant = 'tile' }) => {
  const { t } = useTranslation();
  if (!head) return null;
  const isTile = variant !== 'card';
  // The journey's own picture. It used to be the running theme's whenever one was running, and
  // with several of them running that was a coin toss — this one is the company's face all
  // year, written on /journey beside the name. The flag placeholder is what is left when a
  // company has given neither.
  const image = head.image;
  // Where in the year they are: the programme, and nothing else on this strip.
  const phase = (head.programme && head.programme.name) || '';
  const line = (
    <>
      {image ? (
        <span className="jhead__img" style={{ backgroundImage: `url("${cssUrl(image)}")` }} />
      ) : (
        <span className="jhead__img jhead__img--empty">
          <Icon name="flag" theme="primary" />
        </span>
      )}
      <span className="jhead__body">
        {/* An employee reads this as their company's, not their own, so the fallback wording
            differs even though everything under it is identical. */}
        <span className="jhead__name">
          {head.name || (isTile ? t('yourJourney') : t('whatWeStandFor'))}
        </span>
        {phase && <span className="jhead__phase">{phase}</span>}
      </span>
    </>
  );

  if (isTile) {
    return (
      <div className="stat-tile stat-tile--orange jhead">
        <div className="jhead__top">
          <Link to="/programmes" className="jhead__main">{line}</Link>
          {/* The readiness percentage, big — it is the number this tile replaced, and it was
              being read at half the size of every other number on the grid. */}
          <Link to="/journey" className="jhead__pctlink">
            <span className="jhead__pct stat-tile__num">{pct || 0}%</span>
            <Icon name="angle-right" className="bravos-event__chev" />
          </Link>
        </div>
        <PurposeStrip values={head.values} sdgs={head.sdgs} houses={head.houses} />
      </div>
    );
  }
  return (
    <Card>
      <div className="jhead jhead--card">
        {/* Not a link. It used to open the running theme's own page when the company had written
            one, and an employee cannot reach any of the screens behind the rest of this badge —
            so a card that highlights on hover and then does nothing is worse than a flat one. */}
        <div className="jhead__top">{line}</div>
        <PurposeStrip values={head.values} sdgs={head.sdgs} houses={head.houses} />
      </div>
    </Card>
  );
};

/* The company admin's standing jobs, as numbers. Each tile owns a screen; nothing here
   renders detail, which is exactly why the home got cheap enough to be quick.

   ★ The labels are deliberately shorter than the titles of the screens they open: a tile is
   read at a glance in a grid of a dozen, a page header is read on arrival, and "Manage our
   company's Bravos Members" over a two-digit number was the grid shouting. */
/* ★★ Has this figure arrived, and is it a number?
   ``stats`` is fetched lazily and every figure inside it is fail-safe on its own, so three
   things have to be told apart and only two of them are the same: a figure that is MISSING
   (the fetch has not landed, the read failed, the company has not bought the product) leaves
   its tile exactly as it was before any of this — the count big, the old label. A figure that
   is genuinely ZERO is drawn as a zero, because "nobody has been cheered for one of our values
   yet" is an answer and the tile is where somebody ought to see it.
   Same contract PurposeStats.js has always used for its event figures: hasOwnProperty, never
   truthiness. */
const has = (obj, key) => obj != null && Object.prototype.hasOwnProperty.call(obj, key)
  && typeof obj[key] === 'number';

/* A way in the company does not have on this brand — "Events I'm hosting", the specialists, the
   member badge, Refer a friend are what a Bravos membership brings. On a Hercules Trophy, Pink
   Ladies Games or Fabrica admin home they are drawn here, greyed with a lock and inert, so the
   admin sees the whole platform without being sent into a screen that refuses them. */
const LockedRow = ({ icon, title, hint }) => (
  <Card>
    <div className="bravos-memberslink bravos-memberslink--locked" aria-disabled="true">
      <span className="bravos-memberslink__body">
        <span className="bravos-memberslink__title">
          <Icon name={icon} className="me-2" />
          {title}
        </span>
        {hint && <span className="bravos-memberslink__count">{hint}</span>}
      </span>
      <Icon name="lock" className="bravos-event__chev" />
    </div>
  </Card>
);

/* ★ The FIRST card of the company admin home: our four businesses under the same parent company —
   Hercules Trophy, Pink Ladies Games, Bravos, Fabrica — as four equal tiles, booked or not (an
   awareness generator). Each tile has ONE button:
     - they take part (or took part, or are a Bravos company member): a tick with what that is —
       it opens the game Home on their next event, else their latest one, or their Bravos admin home;
     - they never did: "Discover" — the brand's commercial website, or "I'm interested" without one. */
const BrandTile = ({ b, onManage, onOpenBravos, onInterest, busy }) => {
  const { t } = useTranslation();
  const next = b.upcoming[0];
  const last = b.past[0];
  const member = b.membership && b.membership.active;
  let status = null;
  let open = null;
  if (member) {
    status = b.membership.until
      ? t('brandMemberUntil', { date: formatDate(b.membership.until) }) : t('brandMember');
    open = b.is_current ? null : () => onOpenBravos(b);
  } else if (next) {
    status = t('brandSignedUp', { date: formatDate(next.date) });
    open = () => onManage(next.id);
  } else if (last) {
    status = t('brandTookPart', { date: formatDate(last.date) });
    open = () => onManage(last.id);
  }
  const discover = () => (b.website
    ? window.open(b.website, '_blank', 'noopener') : onInterest(b));
  return (
    <div className="bravos-brand-tile">
      <div className="bravos-brand-tile__head">
        {b.logo_url && <img src={b.logo_url} alt="" className="bravos-brand-tile__logo" />}
        <span className="bravos-brand-tile__name">{b.name}</span>
      </div>
      <p className="bravos-brand-tile__pitch">{t(`brandPitch_${b.model}`)}</p>
      {status ? (
        <button type="button" className="bravos-brand-tile__btn bravos-brand-tile__btn--done"
                disabled={!open || !!busy} onClick={open || undefined}>
          <Icon name="check-circle" /> <span>{status}</span>
        </button>
      ) : (
        <button type="button" className="bravos-brand-tile__btn" onClick={discover}>
          <Icon name={b.website ? 'globe' : 'hand-wave'} />{' '}
          <span>{b.website ? t('brandDiscover') : t('moduleImInterested')}</span>
        </button>
      )}
    </div>
  );
};

const OurBrands = ({ brands, onManage, onOpenBravos, onInterest, busy }) => {
  const { t } = useTranslation();
  if (!brands || !brands.length) return null;
  return (
    <Card className="bravos-ours">
      <Title my="1" className="mt-2">
        <Icon name="trophy" theme="primary" className="me-2" />
        {t('ourBrandsTitle')}
      </Title>
      <div className="bravos-brand-grid">
        {brands.map((b) => (
          <BrandTile key={b.bu_id} b={b} onManage={onManage} onOpenBravos={onOpenBravos}
                     onInterest={onInterest} busy={busy} />
        ))}
      </div>
    </Card>
  );
};

/* The PAID modules of the company admin home and how their tile looks while it is locked. Same
   icon, tone and label as the live tile, so buying one changes the tile and not its place. */
const PAID_TILES = {
  missions: { tone: 'orange', icon: 'flag', label: 'missionsTitle' },
  assessments: { tone: 'purple', icon: 'star', label: 'bravosAssessments' },
  clubs: { tone: 'teal', icon: 'users', label: 'clubsTitle' },
  surveys: { tone: 'pink', icon: 'document', label: 'tileQuestions' },
  leagues: { tone: 'purple', icon: 'leaderboard', label: 'leaguesAndTournaments' },
  cheers: { tone: 'orange', icon: 'hands-clapping', label: 'tileCheers' },
  history: { tone: 'teal', icon: 'chart-line-up', label: 'chTileLabel' },
  heatmap: { tone: 'teal', icon: 'chart-tree-map', label: 'hmTileLabel' },
};

const AdminTiles = ({ tiles, stats, head, isEngagement, feedRecent, photoCount, modules,
                      isBravos = true, onInterest }) => {
  const { t } = useTranslation();
  if (!tiles) return null;
  /* ★ Which modules this company has (``modules`` from the server: the free ones every company
     admin of every brand gets, and the paid ones it bought). An older payload without the list
     falls back to the engagement package, which used to be all-or-nothing. */
  const on = (key) => {
    const m = (modules || []).find((x) => x.key === key);
    return m ? !!m.active : !!isEngagement;
  };
  /* A paid module the company has not got: the same tile, greyed, with a lock — tapping it opens
     the "I'm interested" sheet. Visible on purpose: it is how an admin sees what more there is. */
  const locked = (key) => (
    <StatTile
      key={`locked-${key}`}
      locked
      tone={PAID_TILES[key].tone}
      icon={PAID_TILES[key].icon}
      label={t(PAID_TILES[key].label)}
      onClick={onInterest ? () => onInterest(key) : undefined}
    />
  );
  const signal = tiles.company_signal;
  const tickets = tiles.tickets || {};
  const surveys = tiles.surveys || {};
  const missions = tiles.missions || {};
  const leagues = tiles.leagues || {};
  const cheers = tiles.cheers || {};
  const houses = tiles.houses || {};
  const wall = tiles.awards || {};
  const structure = tiles.structure || {};
  /* The living half. Each of these is the number the screen behind the tile reports, and each
     replaces the inventory as the BIG figure while the inventory drops to the line under it:
     an admin coming back on Monday should read what has HAPPENED, not what was declared. */
  const s = stats || {};
  const sProg = s.programmes || {};
  const sThemes = s.themes || {};
  const sHouses = s.houses || {};
  const sEvents = s.events || {};
  const sSurveys = s.surveys || {};
  const sMissions = s.missions || {};
  const sLeagues = s.leagues || {};
  /* The spread line under the events count: how many different colleagues came to something,
     and how many kinds of event the diary holds. Each half is dropped when it has nothing to
     say, rather than showing a zero — on a tile, "0 of 120" and "0 kinds" both read as a
     defect where the truth is "not started".

     ★ Reach is gated on there being ANY answer on the diary, not on the reach itself. A low
     reach is exactly the number an admin needs to see, so 12 of 120 stays; but a company whose
     events were all filled the old way (a ticket and a player, no RSVP row anywhere — see the
     EventRsvp docstring) has a true zero that means "we don't record it that way", and that is
     an accusation, not a figure. Kinds needs no such gate: the server already sends the key
     only when something is tagged. */
  const eventsNote = [
    has(sEvents, 'people') && sEvents.going && tiles.members
      ? t('tileEventReach', { people: sEvents.people, roster: tiles.members })
      : null,
    has(sEvents, 'kinds') ? t('tileEventKinds', { count: sEvents.kinds }) : null,
  ].filter(Boolean).join(' · ') || undefined;
  return (
    <div className="stat-tiles">
      <JourneyHead head={head} pct={tiles.journey} />
      <PulseStrip activity={s.activity} />
      {!head && (
        <StatTile
          tone="orange"
          to="/journey"
          ring={tiles.journey || 0}
          num={`${tiles.journey || 0}%`}
          label={t('yourJourney')}
        />
      )}
      <StatTile
        tone="teal"
        to="/pulses"
        icon="list-check"
        num={tiles.pulses || 0}
        label={t('tilePulses')}
        sub={t('responsesSoFar')}
      />
      {on('assessments') ? (
        <StatTile
          tone="purple"
          to="/assessments"
          icon="star"
          num={tiles.assessments || 0}
          label={t('bravosAssessments')}
          sub={t('responsesSoFar')}
        />
      ) : locked('assessments')}
      {isBravos && (
        <StatTile
          tone="pink"
          to="/company-tickets"
          icon="ticket"
          num={`${tickets.open || 0}/${tickets.total || 0}`}
          label={t('companyTickets')}
          sub={t('openSeats')}
        />
      )}
      <StatTile
        tone="teal"
        to="/company-members"
        icon="users"
        num={tiles.members || 0}
        label={t('tileMembers')}
      />
      <StatTile
        tone="orange"
        to="/company-signal"
        ring={signal && signal.total ? Math.round((signal.filled * 100) / signal.total) : 0}
        num={signal ? `${signal.filled}/${signal.total}` : '0/25'}
        label={t('tileSignals')}
      />
      {/* ★★ What the company stands for and how it groups its year — four tiles where there was
          one. They were tabs behind a single "What we stand for" card, which meant an admin
          coming to fix a house and an admin coming to declare a value tapped the same tile and
          then had to find their tab. Each is a screen with its own number now.

          The grouping is carried by the tone: PURPLE is the two ways the year is planned, TEAL
          is what the company itself stands for, PINK is how its people are divided.

          ★ The teal one is drawn for EVERY company — its values and goals are the company
          itself, not something it buys. The other three are the engagement product's and get no
          tile at all without it, because a tile that leads to a 403 is worse than no tile. */}
      {on('programmes') && (
        <StatTile
          tone="purple"
          to="/programmes"
          icon="road"
          num={has(sProg, 'going') ? sProg.going : (tiles.programmes || 0)}
          trend={sProg.trend}
          label={has(sProg, 'going') ? t('statsAnsweredYes') : t('ourProgrammes')}
          sub={has(sProg, 'going')
            ? t('tileProgrammeMake', { n: tiles.programmes || 0, events: sProg.events || 0 })
            : undefined}
        />
      )}
      {on('themes') && (
        <StatTile
          tone="purple"
          to="/themes"
          icon="calendar-lines"
          num={has(sThemes, 'going') ? sThemes.going : (tiles.themes || 0)}
          trend={sThemes.trend}
          label={has(sThemes, 'going') ? t('statsAnsweredYes') : t('journeyThemes')}
          sub={has(sThemes, 'going')
            ? t('tileThemeMake', { n: tiles.themes || 0, events: sThemes.events || 0 })
            : undefined}
        />
      )}
      {/* ★★ What we stand for — the values and the goals back on ONE tile, and no longer led by
          cheers.

          Leading with cheers made both tiles about recognition instead of about the company:
          they each read "Cheers naming one" over a number, so two tiles in the same row carried
          the same label and neither said what it was for. The count of values and the fraction
          of goals is what this card is, and it is what you come here to change.

          The cheers are not lost — they are the headline on the values and goals screens
          themselves, where the whole page is about them (see PurposeStats).

          ★ It points at the Company Signal, not at /values: declared values and SDG alignment
          are two DERIVED fields of that record, and the signal screen already sends you on to
          the right editor for each. One destination rather than a fork the tile has to guess. */}
      <StatTile
        tone="teal"
        to="/company-signal"
        icon="heart"
        num={`${tiles.values || 0} · ${tiles.sdgs || 0}/17`}
        label={t('whatWeStandFor')}
        /* Out of seventeen, always: "4" is a number to be pleased with, "4 of 17" is the one
           worth thinking about. The sub-line names the two halves, the way the tickets tile
           labels its own "7/12". */
        sub={t('tileStandForLegend')}
      />
      {/* The sub-line is the half only an admin can act on: a shelf of four houses with half the
          roster still outside them is a job half done, and the count alone never says so. */}
      {on('houses') && (
        <StatTile
          tone="pink"
          to="/houses"
          icon="home-location-alt"
          num={has(sHouses, 'points') ? sHouses.points : (houses.total || 0)}
          label={has(sHouses, 'points') ? t('housePoints') : t('ourHouses')}
          sub={has(sHouses, 'points')
            ? t('tileHouseMake', { n: houses.total || 0 })
            : undefined}
          /* ★ No trend here, and it is not an oversight: a house total is a STANDING, and the
             raw sum reads a column with no date on it. "+40 this week" would be invented.
             The coverage line stays — it is the half only an admin can act on. */
          note={houses.roster
            ? t('houseCoverage', { assigned: houses.placed || 0, total: houses.roster })
            : undefined}
        />
      )}
      {/* ★ Into the PROGRAMME the company is in right now (the server picks it), not the flat
          calendar: that screen holds every event under it, past included, and lets you switch
          programme at the top. The flat calendar is still what an employee gets.

          ★★ This one leads with the COUNT. It used to lead with yes-answers — and so does the
          programmes tile above it, over what is the same set of events whenever every event
          hangs under a programme, which is the normal case. Two tiles saying one number. The
          count is also the number that moves: running more events is what the product is for.
          No trend badge either; see the note where the server builds the arrows.

          The line under it is how many DIFFERENT colleagues came to something, which is a
          different question from how many yes-answers there were (311 of those belong to 56
          people platform-wide), plus how varied the diary is. Both vanish when there is
          nothing to say rather than showing a zero. */}
      {on('events') && (
        <StatTile
          tone="purple"
          to="/programme"
          icon="calendar-star"
          num={has(sEvents, 'total') ? sEvents.total : (tiles.events || 0)}
          label={t('ourEvents')}
          sub={has(sEvents, 'total')
            ? t('tileEventSplit', { past: sEvents.past || 0, upcoming: sEvents.upcoming || 0 })
            : undefined}
          note={eventsNote}
        />
      )}
      {/* Clubs — the running club, the book club. The number is how many the company has; the
          tile opens the overview where they are founded and where each one's activity shows. */}
      {on('clubs') ? (
        <StatTile
          tone="teal"
          to="/clubs/manage"
          icon="users"
          num={tiles.clubs || 0}
          label={t('clubsTitle')}
        />
      ) : locked('clubs')}
      {/* The question library, next to the diary it runs on — a survey exists to be asked at
          an event. The big number is how many sets we keep; the line under it is how many are
          open right now, which is the half an admin can act on. */}
      {on('surveys') ? (
        <StatTile
          tone="pink"
          to="/company-surveys"
          icon="document"
          num={has(sSurveys, 'answers') ? sSurveys.answers : (surveys.total || 0)}
          trend={sSurveys.trend}
          label={has(sSurveys, 'answers') ? t('surveyAnswers') : t('tileQuestions')}
          sub={has(sSurveys, 'answers')
            ? t('tileSurveyMake', { n: surveys.total || 0 })
            : undefined}
          note={t('surveysRunningNow', { n: surveys.open || 0 })}
        />
      ) : locked('surveys')}
      {/* Missions (F3) — the third thing a company runs, beside the diary and the questions.
          The big number is how many are open; the line under it is how many answers are waiting
          for a yes, which is the half only an admin can act on. A mission answer is the only
          content here that nobody sees until it is approved, so an unwatched queue is people
          posting into silence. */}
      {on('missions') ? (
        <StatTile
          tone="orange"
          to="/company-missions"
          icon="flag"
          num={has(sMissions, 'approved') ? sMissions.approved : (missions.open || 0)}
          trend={sMissions.trend}
          label={has(sMissions, 'approved') ? t('missionsApproved') : t('missionsTitle')}
          sub={has(sMissions, 'approved')
            ? t('tileMissionMake', { n: missions.open || 0, answers: sMissions.answers || 0 })
            : undefined}
          /* Tinted, because unlike every other line on this grid it is not a fact but a queue:
             a mission answer is the one thing here nobody sees until an admin says yes. */
          note={missions.pending ? t('missionsWaiting', { n: missions.pending }) : undefined}
          noteTone="warn"
        />
      ) : locked('missions')}
      {/* Leagues and tournaments (F6) — the competitions this company runs. The big number is
          how many are still to be played; the line under it is the half only an admin can act
          on: league days that are over and have never been scored into a standing. That is the
          silent failure of the whole feature — the events happened, people asked who is winning,
          and the table never moved.

          ★ A single day that is a whole competition counts here too. It is the same thing at a
          smaller size — a league is a competition spread over days that are not next to each
          other — and a tile counting only the seasons read as "we run no competitions" to a
          customer running six of them. */}
      {on('leagues') ? (
        <StatTile
          tone="purple"
          to="/leagues"
          icon="leaderboard"
          num={has(sLeagues, 'people')
            ? sLeagues.people
            : (leagues.running || 0) + (leagues.tournaments || 0)}
          trend={sLeagues.trend}
          label={has(sLeagues, 'people') ? t('leaguesTakingPart') : t('leaguesAndTournaments')}
          sub={has(sLeagues, 'people')
            ? t('tileLeagueMake', {
              n: (leagues.running || 0) + (leagues.tournaments || 0),
              results: sLeagues.results || 0,
            })
            : undefined}
          note={leagues.unscored
            ? t('leaguesUnscored', { n: leagues.unscored })
            : t('leaguesRunning')}
          noteTone={leagues.unscored ? 'warn' : undefined}
        />
      ) : locked('leagues')}
      {/* Cheers (F7) — is anybody recognising anybody here. The big number is how many cheers
          people chose to SHOW, which is the only kind that exists as far as any reader is
          concerned; a pending one is private between the two it is between. The line under it is
          how many colleagues one of them is about, and that is the half that answers the
          question an admin actually has: whether this is a culture or three enthusiasts.

          ★ Never who is cheered MOST. A visible personal ranking is a culture decision the owner
          has kept shut, and a tile is exactly where one would have leaked in. */}
      {on('cheers') ? (
        <StatTile
          tone="orange"
          to="/engagement?tab=cheers"
          icon="hands-clapping"
          num={cheers.shown || 0}
          label={t('tileCheers')}
          sub={t('cheerStatReceivers', { n: cheers.receivers || 0 })}
        />
      ) : locked('cheers')}
      {/* ★★ The wall of fame — the only tile on this grid that looks BACKWARDS on purpose. Every
          other figure here is a level ("where are we now") or a queue ("what needs me"); this one
          is what already happened and cannot be taken away, which is exactly why a company wants
          it on the wall. It sits beside the cheers for that reason: both are about what people
          DID, and a prize is the loudest of the two.

          Three numbers on one tile: prizes over events, with the people count as the line under
          it. Drawn only when there IS something on the wall — a tile promising a hall of fame and
          opening an empty room is worse than no tile, the same rule the photo wall follows. */}
      {on('awards') && (
        <StatTile
          tone="orange"
          to="/wall-of-fame"
          icon="trophy"
          num={wall.total || 0}
          label={t('wallOfFame')}
          sub={t('wallTileSub', { events: wall.events || 0 })}
          note={wall.people ? t('wallTilePeople', { n: wall.people }) : undefined}
        />
      )}
      {/* How many of our people have anything to their name. Deliberately a fraction: "12" is a
          number to be pleased with, "12 of 40" is the one worth acting on. */}
      {on('engaged') && (
        <StatTile
          tone="teal"
          to="/engagement"
          icon="chart-histogram"
          num={`${tiles.engaged || 0}/${tiles.members || 0}`}
          label={t('engagementTitle')}
        />
      )}
      {/* The stream counts a WEEK, so the window is named under it: a bare "0" beside "Feed"
          would read as an empty feed rather than a quiet one. */}
      <StatTile
        tone="pink"
        to="/company-feed"
        icon="megaphone"
        num={feedRecent || 0}
        label={t('tileFeed')}
        sub={t('thisWeek')}
      />
      {/* The wall's number is a TOTAL — a wall is a collection. Only when there ARE pictures:
          a tile promising a wall and opening an empty room is worse than no tile. */}
      {on('photos') && (
        <StatTile
          tone="purple"
          to="/photo-wall"
          icon="camera"
          num={photoCount}
          label={t('photoWallTitle')}
        />
      )}
      {/* ★ The one tile on this grid without a number on it, and the only one that does not
          answer "where are we". Every figure above is a LEVEL — where the company stands
          tonight — and a level is overwritten by tomorrow's unless something writes it down.
          This opens what the nightly snapshot has been writing down, which is the one question
          the grid itself can never answer. */}
      {on('history') ? (
        <StatTile
          tone="teal"
          className="stat-tile--action"
          icon="chart-line-up"
          to="/company-history"
          label={t('chTileLabel')}
        />
      ) : locked('history')}
      {/* ★ The org chart behind the heatmap: how many of the company's people sit in it. Same
          gate as the heatmap — no tree, no tile. The warn note is the half an admin can act on:
          somebody who sits in no unit is somebody the heatmap cannot see. */}
      {on('structure') && (
        <StatTile
          tone="teal"
          icon="sitemap"
          to="/company-structure"
          num={structure.placed || 0}
          label={t('osTileLabel')}
          sub={structure.units
            ? t('osTileSub', { people: structure.people || 0, units: structure.units })
            : undefined}
          note={structure.units && structure.people > structure.placed
            ? t('osTileUnplaced', { n: structure.people - structure.placed })
            : undefined}
          noteTone="warn"
        />
      )}
      {/* ★ Gated on the org tree, not on the product. The heatmap reports per org unit, so
          without a tree it can only be an empty grid — and a tile that always leads to nothing
          teaches an admin to stop pressing tiles. The server answers that with two small
          indexed reads (``coverage``); no tree, no tile. */}
      {on('heatmap') ? (
        <StatTile
          tone="teal"
          className="stat-tile--action"
          icon="chart-tree-map"
          to="/company-heatmap"
          label={t('hmTileLabel')}
        />
      ) : locked('heatmap')}
    </div>
  );
};

const EventRow = ({ event, view, onOpen, onClaim, onBook, onRsvp,
                    onCopyFriend, onCancel, onInviteNetwork, isEcosystem,
                    copied, claiming, canceling, rsvping }) => {
  const { t } = useTranslation();
  const upcomingMember = view === 'member' && !event.is_past && !event.attending;
  // ★ An event our OWN company runs is ANSWERED, never bought — it costs no seat. It sits in
  // the same calendar as the Labs, so the row has to carry the two answer buttons the company
  // calendar carries; a "Book now" on a townhall was simply the wrong verb.
  const isOwn = !!event.is_company;
  // An employee whose company holds an open pool seat can claim it right here; otherwise a
  // member books the Lab through the in-app checkout (Book now). The row itself opens the event.
  const showClaim = upcomingMember && !isOwn && event.claimable;
  const showBook = upcomingMember && !isOwn && !event.claimable;
  // Both answers stay on the row after you gave one, so you can change your mind; they only go
  // once the deadline has passed, because past it the server refuses either way.
  const showRsvp = view === 'member' && !event.is_past && isOwn && !event.registration_closed;
  // A member can give a seat up: one from the company pool goes straight back to that pool,
  // one they bought themselves becomes a paid credit in their wallet. The backend only sets
  // cancelable while that is still allowed, so the button disappears past the deadline.
  const showCancel = view === 'member' && !event.is_past && event.attending && event.cancelable;
  const cancelHint = event.cancel_kind === 'credit' && event.cancel_deadline
    ? t('cancelUntil', { date: formatDate(event.cancel_deadline) })
    : undefined;
  return (
    <div
      className="bravos-event"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
    >
      <span
        className="bravos-event__thumb"
        style={{ backgroundImage: `url("${event.image_link || fallbackEvent}")` }}
      />
      <span className="bravos-event__body">
        <span className="bravos-event__name">
          {event.name}
          {event.is_season && <span className="bravos-event__badge">★</span>}
        </span>
        {/* Day, then the start time, then where it is — the three things you check before
            deciding whether you can be there. */}
        <span className="bravos-event__meta">
          {[eventDayLabel(event), eventStartLabel(event), event.location_name]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {/* Who is hosting it and what it is about, on their own line under the date. */}
        {(event.host_names || event.subject) && (
          <span className="bravos-event__sub">
            {[event.host_names ? t('hostedByName', { name: event.host_names }) : null,
              event.subject].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
      <span className="bravos-event__actions">
        {event.attending && (
          <span className="bravos-event__pill bravos-event__pill--in">{t('attendingBadge')}</span>
        )}
        {showClaim && (
          <button
            type="button"
            className="bravos-event__claim bravos-event__act"
            onClick={(e) => { e.stopPropagation(); onClaim(event.id); }}
            disabled={claiming === event.id}
          >
            {claiming === event.id ? '…' : t('registerForLab')}
          </button>
        )}
        {showBook && (
          <button
            type="button"
            className="bravos-event__claim bravos-event__act"
            onClick={(e) => { e.stopPropagation(); onBook(event.id); }}
          >
            {t('bookNow')}
          </button>
        )}
        {showRsvp && (
          <>
            <button
              type="button"
              className={`bravos-event__rsvp bravos-event__act${
                event.my_rsvp === 'going' ? ' is-on' : ''}`}
              onClick={(e) => { e.stopPropagation(); onRsvp(event.id, 'going'); }}
              disabled={rsvping === event.id}
            >
              <Icon name="check" className="me-1" /> {t('imComing')}
            </button>
            <button
              type="button"
              className={`bravos-event__rsvp bravos-event__act${
                event.my_rsvp === 'not_going' ? ' is-on' : ''}`}
              onClick={(e) => { e.stopPropagation(); onRsvp(event.id, 'not_going'); }}
              disabled={rsvping === event.id}
            >
              {t('imNotComing')}
            </button>
          </>
        )}
        {view === 'member' && !event.is_past && isOwn && event.registration_closed && (
          <span className="bravos-event__note">{t('registrationClosed')}</span>
        )}
        {showCancel && (
          <button
            type="button"
            className="bravos-event__cancel"
            title={cancelHint}
            onClick={(e) => { e.stopPropagation(); onCancel(event.id, event.cancel_kind); }}
            disabled={canceling === event.id}
          >
            {canceling === event.id ? '…' : t('cancelBooking')}
          </button>
        )}
        {view === 'member' && !event.is_past && !isOwn && event.friend_invite && (
          <button
            type="button"
            className="bravos-event__act bravos-event__invite bravos-event__invite--friend"
            onClick={(e) => { e.stopPropagation(); onCopyFriend(event); }}
          >
            <Icon name={copied === `friend-${event.id}` ? 'check' : 'paper-plane'} className="me-1" />
            {copied === `friend-${event.id}` ? t('copied') : t('inviteFriend')}
          </button>
        )}
        {view === 'member' && !event.is_past && !isOwn && isEcosystem && (
          <button
            type="button"
            className="bravos-event__act bravos-event__invite bravos-event__invite--network"
            onClick={(e) => { e.stopPropagation(); onInviteNetwork(event.id); }}
          >
            <Icon name="users" className="me-1" />
            {t('inviteMyNetwork')}
          </button>
        )}
      </span>
      <Icon name="angle-right" className="bravos-event__chev" />
    </div>
  );
};

/* Articles: ours and, for a Bravos company that added its own feeds, theirs — one stream.
   An item from a company feed is labelled and the card says plainly that we do not review those,
   because we don't and a reader should not have to guess whose words they are reading. */
const ContentFeed = ({ items, more, onMore, onManage, onSubmitArticle, manageLocked = false }) => {
  const { t } = useTranslation();
  if (!items || !items.length) return null;
  const hasCompanyItems = items.some((it) => it.source === 'company');
  return (
    <Card>
      <Title my="1" className="mt-2">
        <Icon name="newspaper" theme="primary" className="me-2" />
        {t('latestContent')}
      </Title>
      {items.map((it, i) => (
        <a
          key={i}
          className="bravos-feed"
          href={it.url || '#'}
          target="_blank"
          rel="noopener noreferrer"
        >
          {it.image && (
            <span
              className="bravos-feed__thumb"
              style={{ backgroundImage: `url("${it.image}")` }}
            />
          )}
          <span className="bravos-feed__body">
            <span className="bravos-feed__title">
              {it.title}
              {it.source === 'company' && (
                <span className="bravos-feed__source">{it.source_label || t('ourTheme')}</span>
              )}
              {/* The admin home mixes every brand's articles: each names whose it is. */}
              {it.source === 'brand' && it.source_label && (
                <span className="bravos-feed__source">{it.source_label}</span>
              )}
            </span>
            {it.summary && <span className="bravos-feed__summary">{it.summary}</span>}
          </span>
          <Icon name="angle-right" className="bravos-event__chev" />
        </a>
      ))}
      {/* One row of ways out of the card: read the rest, or — for an approved specialist —
          put your own article forward. The form itself sits on the profile screen, which is
          where nobody looked for it. */}
      {(more || onSubmitArticle || onManage || manageLocked) && (
        <div className="bravos-feed__actions">
          {more && (
            <button type="button" className="bravos-submit bravos-submit--ghost" onClick={onMore}>
              {t('showMore')} <Icon name="angle-right" />
            </button>
          )}
          {onSubmitArticle && (
            <button type="button" className="bravos-submit bravos-submit--ghost"
                    onClick={onSubmitArticle}>
              <Icon name="plus" /> {t('addOwnBlogpost')}
            </button>
          )}
          {onManage && (
            <button type="button" className="bravos-submit bravos-submit--ghost" onClick={onManage}>
              <Icon name="newspaper" /> {t('ourBlogs')}
            </button>
          )}
          {/* Off Bravos the company blogs are not part of the package: shown, never a way in. */}
          {manageLocked && (
            <button type="button" className="bravos-submit bravos-submit--ghost bravos-locked"
                    disabled>
              <Icon name="newspaper" /> {t('ourBlogs')} <Icon name="lock" />
            </button>
          )}
        </div>
      )}
      {hasCompanyItems && <p className="bravos-feed__disclaimer">{t('feedDisclaimer')}</p>}
    </Card>
  );
};

/* An individual member's tickets — a single link row in the same shape as the other cards on
   this home ("Events I'm hosting", the badge, Refer a friend): what they hold and one tap to
   /wallet, where the signed QR the check-in reads lives. The count adds the tickets already tied
   to a Lab to the membership pool they haven't registered yet ("available"), because both are
   theirs. Fetches GET /api/my-tickets itself.

   A member who bought the membership gets ONE card, not two: the pack ("8 of 11 Lab tickets left")
   and the tickets it hands out were the same block read twice, so the membership speaks here and
   the chevron still opens the wallet. Without a membership it stays the plain ticket count.
   Renders nothing when there is neither. */
const WalletTickets = ({ membership }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    apiRequest('GET', '/my-tickets')
      .then((d) => {
        const held = ((d && d.groups) || []).reduce((n, g) => n + ((g.tickets || []).length), 0)
          + ((d && d.unassigned) || []).length;
        const available = ((d && d.available) || [])
          .reduce((n, a) => n + (a.count || 0), 0);
        setCount(held + available);
      })
      .catch(() => setCount(0));
  }, []);

  if (!count && !membership) return null;
  // packSize can be absent on an older payload, and then it falls back to the bare count.
  const left = membership && membership.tickets_available;
  const packSize = membership && membership.tickets_total;
  const used = (membership && membership.tickets_used) || 0;
  return (
    <Card>
      <button type="button" className="bravos-memberslink" onClick={() => navigate('/wallet')}>
        <span className="bravos-memberslink__body">
          <span className="bravos-memberslink__title">
            <Icon name="ticket" theme="primary" className="me-2" />
            {membership ? t('yourMembership') : t('yourTickets')}
          </span>
          <span className="bravos-memberslink__count">
            {membership ? (
              <>
                {packSize
                  ? t('ticketsLeftOf', { n: left, total: packSize })
                  : t('ticketsLeft', { n: left })}
                {used > 0 ? ` · ${t('ticketsUsedSoFar', { n: used })}` : ''}
              </>
            ) : t('ticketsHeldCount', { n: count })}
          </span>
          {membership && (
            <span className="bravos-memberslink__note">{t('ticketsNeverExpire')}</span>
          )}
        </span>
        <Icon name="angle-right" className="bravos-event__chev" />
      </button>
    </Card>
  );
};

/* Contact-person view of the company's ticket POOL — per type (Lab / Off record): how many are
   still open (event-less, ready to hand out) vs allocated to people, of the total held. These
   tickets move between Labs: an invite stamps a Lab onto one; a cancel returns it here. */
/* "Become an individual member". Two shapes, driven by membership_offer.state (the backend omits
   the key entirely when the viewer's company already pays, so this renders nothing for them), and
   the two shapes sit in DIFFERENT places on the home — the render sites pick by state:
     offer — the pitch, ABOVE THE CALENDAR, because that calendar is where the decision happens:
             most people arrive by coming to one Lab for free and are never asked again. It shows
             what the membership contains (read off the rate, so it can never drift from what the
             sale hands out), the price, and how many Labs they have joined so far. That count is
             deliberately neutral: someone on their fifth free visit reads it themselves.
     owned — nothing here: the wallet row above says it (WalletTickets), because what the
             membership has left and the tickets it hands out are one card, not two. */
const MembershipOffer = ({ offer, onJoin, onJoinCompany }) => {
  const { t } = useTranslation();
  if (!offer || offer.state === 'owned') return null;
  const { rate, currency, labs_attended: labs } = offer;
  const seats = (rate?.tickets || [])
    .filter((x) => x.kind === 'seat')
    .reduce((n, x) => n + (x.quantity || 0), 0);
  // Rates are stored NET. A shop window quotes what the buyer pays, so VAT goes in — and the
  // arithmetic + formatting deliberately mirror the public storefront (Storefront.js), because
  // both quote the same rate and must never show two different prices. The checkout still splits
  // net / VAT / total.
  const vat = offer.with_vat && rate?.vat_rate ? Number(rate.vat_rate) : 0;
  const net = Number(rate?.effective_price ?? rate?.unit_price ?? 0);
  const price = (net * (1 + vat / 100)).toFixed(2);

  return (
    <Card>
      <div className="bravos-join">
        <Title my="1" className="mt-2">
          <Icon name="star" theme="primary" className="me-2" />
          {t('joinTitle')}
        </Title>
        <p className="bravos-join__lead">
          {seats > 0 ? t('joinLead', { n: seats }) : t('joinLeadPlain')}
        </p>
        <ul className="bravos-join__benefits">
          <li>{t('joinBenefitNeverExpire')}</li>
          <li>{t('joinBenefitOneTap')}</li>
          <li>{t('joinBenefitCommunity')}</li>
        </ul>
        {rate && (
          <div className="bravos-join__price">
            <span className="bravos-join__amount">{currency} {price}</span>
            <span className="bravos-join__per">
              {rate.name}{vat ? ` · ${t('inclVat')}` : ''}
            </span>
          </div>
        )}
        {labs > 0 && (
          <p className="bravos-join__note">{t('labsJoinedSoFar', { count: labs })}</p>
        )}
        <button type="button" className="bravos-submit" onClick={onJoin}>
          {t('joinCta')}
        </button>
        {offer.has_company_option && (
          <button type="button" className="bravos-join__alt" onClick={onJoinCompany}>
            {t('joinWithCompany')}
          </button>
        )}
      </div>
    </Card>
  );
};

const MemberHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  // The member's own league list. Its own fetch rather than a field on the home payload: an
  // employee whose company runs none pays nothing for it, and this home is already the
  // platform's widest read.
  const [myLeagues, setMyLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [canceling, setCanceling] = useState(null);
  const [rsvping, setRsvping] = useState(null);
  const [claimNotice, setClaimNotice] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [showMeetQr, setShowMeetQr] = useState(false);

  // Copy a warm, ready-to-send message + the link (not a bare URL), so it drops
  // straight into WhatsApp/email and the recipient sees why they got it.
  const copyLink = (key, link, message) => {
    const text = message ? `${message}\n${link}` : link;
    const done = () => { setCopied(key); setTimeout(() => setCopied(null), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };
  const copyFriend = (event) =>
    event.friend_invite && copyLink(`friend-${event.id}`, event.friend_invite.link,
                                    t('referShareText', { event: event.name }));

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/home')
      .then((res) => {
        if (res && res.redirect) {
          navigate(res.redirect);
          return;
        }
        setData(res);
      })
      .catch((err) => {
        // ★ Signed out → the login, and back here after it. apiRequest only bounces a 401 for a
        // browser that WAS signed in, so somebody arriving cold — a mail link opened in another
        // browser, an expired or forwarded sign-in link — got a white page instead.
        if (NO_SESSION.includes(err && err.message)) {
          sessionStorage.setItem('redirect', '/member-home');
          navigate('/login');
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  /* ★★ The LIVING numbers behind the admin tiles, fetched after the home has painted.
     Deliberately not part of /bravos/home: the sweep behind it reads the whole calendar, the
     survey library, the mission answers and the house standing, and the one property that keeps
     this screen quick is that the home payload does none of that. So the grid draws itself
     immediately with the counts it already has and the tiles fill in a moment later — the same
     bargain MyCompetition and CommunityStats on this page already make.
     Silent and fail-safe: an admin whose stats read fails simply keeps yesterday's tiles. */
  const [tileStats, setTileStats] = useState(null);
  const isContactView = !!(data && data.view === 'contact');
  // The "I'm interested" sheet behind a locked tile ({module}) or an unbooked brand ({brand}).
  const [interest, setInterest] = useState(null);
  const [managing, setManaging] = useState(null);
  const openInterest = (key) => setInterest({ module: key });
  /* "Manage current event" / a past participation: the server re-scopes the session to that
     event (POST /api/manage-event) and the game Home opens on it. A full load, like the view
     switch below, so nothing on the page keeps the old session in memory. */
  /* Their Bravos company membership, from a games brand's admin home: the role picker's own path
     (POST /api/role with the bravosCompany card) re-scopes the session to the Bravos season, and
     this same page then opens as their Bravos admin home. */
  const openBravos = (b) => {
    if (managing || !data || !data.company_id) return;
    setManaging(b.bu_id);
    apiRequest('POST', '/role', { key: 'bravosCompany', bu_id: b.bu_id, company_id: data.company_id })
      .then((res) => { window.location.href = (res && res.redirect) || '/member-home'; })
      .catch(() => setManaging(null));
  };
  const manageEvent = (eventId) => {
    if (managing) return;
    setManaging(eventId);
    apiRequest('POST', '/manage-event', { event_id: eventId })
      .then((res) => { window.location.href = (res && res.redirect) || '/event-home'; })
      .catch(() => setManaging(null));
  };
  // Both locks are left set by the full-page navigation, so "Back" from the game Home restores
  // this page with every brand tile (and the view switch) still disabled. See the hook.
  useResetSubmittingOnRestore(setManaging);
  useResetSubmittingOnRestore(setSwitching);
  useEffect(() => {
    if (!isContactView) return;
    apiRequest('GET', '/bravos/home/stats').then(setTileStats).catch(() => {});
  }, [isContactView]);

  /* People waiting for the admin's yes (a team or community link they filled in). The banner used
     to live on the game Home only, which an admin of any brand now reaches one tap later — so a
     request could sit unseen. The yes itself is on /company-members. Silent when there are none. */
  const [pendingJoin, setPendingJoin] = useState(0);
  useEffect(() => {
    if (!isContactView) return;
    apiRequest('GET', '/join-requests')
      .then((res) => setPendingJoin(((res && res.requests) || []).length))
      .catch(() => setPendingJoin(0));
  }, [isContactView]);

  /* The member's leagues, fetched separately from the home payload. Fail-safe and silent: an
     employee of a company that runs none gets an empty list and no card, and a failure here must
     never cost them the rest of their home. */
  useEffect(() => {
    apiRequest('GET', '/company/leagues/mine')
      .then((res) => setMyLeagues((res && res.leagues) || []))
      .catch(() => setMyLeagues([]));
  }, []);

  const claimSeat = (eventId) => {
    if (claiming) return;
    setClaiming(eventId);
    setClaimNotice(null);
    apiRequest('POST', '/bravos/claim-seat', { event_id: eventId })
      .then(() => { setClaimNotice(t('seatClaimed')); load(); })
      .catch((err) => setClaimNotice(t(err.message, { defaultValue: t('claimFailed') })))
      .finally(() => setClaiming(null));
  };

  // "I'm coming" / "I'm not coming" on one of our OWN company's events, answered straight from
  // the calendar. Same endpoint the company calendar posts to — one place decides whether the
  // answer is still allowed and what a yes mints (comp seat + player).
  const answerRsvp = (eventId, status) => {
    if (rsvping) return;
    setRsvping(eventId);
    setClaimNotice(null);
    apiRequest('POST', `/company/event/${eventId}/rsvp`, { status })
      .then(() => load())
      .catch((err) => setClaimNotice(apiErrorText(t, err)))
      .finally(() => setRsvping(null));
  };

  const cancelBooking = (eventId, kind) => {
    if (canceling) return;
    // Giving up a seat you paid for is not undoable and never refunded, so say what happens
    // to it before doing it. A company pool seat costs the member nothing — no prompt.
    if (kind === 'credit' && !window.confirm(t('cancelCreditConfirm'))) return;
    setCanceling(eventId);
    setClaimNotice(null);
    apiRequest('POST', '/bravos/cancel-booking', { event_id: eventId })
      .then((res) => {
        setClaimNotice(res && res.kind === 'credit' ? t('creditIssued') : t('bookingCanceled'));
        load();
      })
      .catch((err) => setClaimNotice(t(err.message, { defaultValue: t('cancelFailed') })))
      .finally(() => setCanceling(null));
  };

  // Flip between the admin and the personal view of this same page. POST /api/role rewrites
  // the JWT (it re-derives the person's cards and matches the key server-side), so the switch
  // is the role picker's own path — just without the round trip through it. The NavBar reads
  // its role from sessionStorage, so keep that in step or the bottom bar keeps the old icon.
  const switchView = () => {
    const sw = data && data.switch_view;
    if (!sw || switching) return;
    setSwitching(true);
    apiRequest('POST', '/role', {
      key: sw.key, bu_id: sw.bu_id, company_id: sw.company_id,
    })
      .then((res) => {
        sessionStorage.setItem('role', sw.to === 'admin' ? 'contactPerson' : 'bravosMember');
        window.location.href = (res && res.redirect) || '/member-home';
      })
      .catch(() => setSwitching(false));
  };

  // Somebody who manages two companies (a hotel and its sister company): the session holds one
  // at a time, so re-scope it to the other and reload this page on it. Same endpoint as the
  // switcher on the game Home.
  const switchCompany = (companyId) => {
    if (switching || !companyId || (data && sameId(companyId, data.company_id))) return;
    setSwitching(true);
    apiRequest('POST', '/switch-company', { company_id: companyId })
      .then((res) => { window.location.href = (res && res.redirect) || '/home'; })
      .catch(() => setSwitching(false));
  };

  const brand = data && data.brand;
  const view = (data && data.view) || 'member';
  // A Hercules Trophy, Pink Ladies Games or Fabrica admin on the one company admin home.
  const offBravos = !!(data && data.is_bravos === false);
  const calendar = (data && data.calendar) || [];
  // Upcoming: soonest first. Past: keep the API order (most recent first).
  const upcoming = calendar
    .filter((e) => !e.is_past)
    .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  const past = calendar.filter((e) => e.is_past)
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  const openEvent = (id) => navigate(`/public-event/${id}`);
  /* Where the missions card goes — the chevron and the strip of pictures under it land in the
     same place, because they are one way in and not two. An admin gets the screen whole; anybody
     else opens on what is left to do, or on the answers when there is nothing left. */
  const missionsTo = () => (
    view === 'contact' ? '/company-missions'
      : ((data && data.missions_todo > 0)
        ? '/company-missions?tab=open' : '/company-missions?tab=done'));
  // A member books a Lab through the dedicated in-app checkout (pre-filled + voucher +
  // payment) instead of hopping to the public storefront.
  const bookLab = (id) => navigate(`/book/${id}`);
  // Ecosystem members invite their whole network to a Lab on the My Network screen — carry the
  // Lab so it lands pre-focused (the batch invite there acts on the selected Lab).
  const inviteNetwork = (id) => navigate(`/my-network?lab=${id}`);

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          {brand && brand.logo_url && (
            <img className="bravos-brand__logo" src={brand.logo_url} alt="" />
          )}
          {/* The viewer's own photo, small, next to the BU logo — taps through to their
              profile. The logo already says which BU this is, so no "Bravos" text below. */}
          {data && data.photo_url && (
            <button type="button" className="bravos-brand__avatar-btn"
                    onClick={() => navigate('/profile')} aria-label={t('viewProfile')}>
              <img className="bravos-brand__avatar" src={data.photo_url} alt="" />
            </button>
          )}
          <div className="bravos-brand__text">
            <Title my="0" align="left"
                   className={`mt-1 bravos-brand__welcome ${
                     (data && data.first_name && data.first_name.length > 12)
                       ? 'bravos-brand__welcome--long' : ''}`}>
              {data && data.first_name
                ? t('welcomeName', { name: data.first_name })
                : t('memberHomeTitle')}
            </Title>
            {data && data.ecosystem_roles && data.ecosystem_roles.length > 0 && (
              <div className="bravos-brand__roles">
                {data.ecosystem_roles.map((r) => (
                  <span key={r.id} className="bravos-role-chip"
                        style={r.color ? { borderColor: r.color, color: r.color } : {}}>
                    {r.name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* Someone who leads the holding company AND holds the membership owns two cards
              on the /role selector, and both land on THIS page — so the flip belongs here,
              beside the bell, rather than out at the role picker and back. Absent (and the
              header unchanged) for everyone who only holds one card. */}
          {data && data.switch_view && (
            <button type="button" className="bravos-brand__switch"
                    onClick={switchView} disabled={switching}
                    title={data.switch_view.to === 'admin'
                      ? t('switchToAdmin') : t('switchToMember')}>
              <Icon name={data.switch_view.to === 'admin' ? 'user-crown' : 'trophy'}
                    theme="primary" />
              <span className="bravos-brand__switch-label">
                {data.switch_view.to === 'admin' ? t('switchToAdmin') : t('switchToMember')}
              </span>
            </button>
          )}
          {/* How far their own profile is — one tap onto the field that is still open. Beside
              the bell because both are the same kind of thing: something waiting on them. It
              disappears entirely once the profile is filled in, so a finished member gets the
              header back. */}
          <ProfileCompleteness className="bravos-brand__pc" />
          <NotificationBell className="bravos-brand__bell" />
        </div>
      </Card>

      {/* Which company this admin home is about, when they manage more than one. The server
          only sends the list for two or more, so a single-company admin never sees it. */}
      {data && data.my_companies && data.my_companies.length > 1 && (
        <div className="member-company-switch mb-3">
          <span className="member-company-switch__label">
            <Icon name="building" className="me-2" />{t('switchCompanyTitle')}
          </span>
          <select value={(data.my_companies.find((c) => sameId(c.id, data.company_id)) || {}).id || ''}
                  disabled={switching}
                  aria-label={t('switchCompanySub')}
                  onChange={(e) => switchCompany(e.target.value)}>
            {data.my_companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.is_community ? ` · ${t('communityBadge')}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* A CLUB session ("Plan the club's events" on a club page): say whose calendar the
          company-events screens are running now, and offer the way back. */}
      {data && data.club_context && (
        <ManagingBar clubId={data.club_context.id} name={data.club_context.name} />
      )}

      {/* Modal-flagged notifications (a back-office announcement sent as a pop-up).
          The participant home has carried this since the final-instructions modal;
          a Bravos member never sees that home, so it is mounted here too. */}
      <AnnouncementModal />
      <ModuleInterest
        title={interest ? (interest.brand ? interest.brand.name
          : (PAID_TILES[interest.module] ? t(PAID_TILES[interest.module].label) : null)) : null}
        module={interest && interest.module}
        brandId={interest && interest.brand && interest.brand.bu_id}
        text={interest && interest.brand ? t('brandInterestText', { brand: interest.brand.name }) : undefined}
        onClose={() => setInterest(null)}
      />

      {loading ? (
        <Card><span className="auth__spinner" /></Card>
      ) : (
        <>
          {/* ADMIN: the six tiles. MEMBER: what their company stands for, then their own
              pulses inline — the one thing they came for stays one tap away. */}
          {data && view === 'contact' && pendingJoin > 0 && (
            <Card>
              <div className="bravos-adminvite">
                <div className="bravos-adminvite__body">
                  <span className="bravos-adminvite__title">
                    {pendingJoin} · {t('pendingJoinTitle')}
                  </span>
                  <span className="bravos-adminvite__note">{t('pendingJoinSub')}</span>
                </div>
                <button type="button" className="bravos-copy"
                        onClick={() => navigate('/company-members')}>
                  <Icon name="user-check" /> {t('reviewRequests')}
                </button>
              </div>
            </Card>
          )}
          {data && view === 'contact' && (
            <OurBrands brands={data.our_brands} onManage={manageEvent} busy={managing}
                       onOpenBravos={openBravos}
                       onInterest={(b) => setInterest({ brand: b })} />
          )}
          {data && view === 'contact' && (
            <AdminTiles tiles={data.tiles} stats={tileStats} head={data.journey_head}
                        isEngagement={!!data.is_engagement} modules={data.modules}
                        isBravos={data.is_bravos !== false} onInterest={openInterest}
                        feedRecent={data.feed_recent} photoCount={data.photo_count} />
          )}

          {/* The same badge, as a card: an employee reads their company's programme and what it
              stands for. Renders nothing at all when the company filled in none of it — a blank
              strip would be worse than no strip. */}
          {data && view === 'member' && (
            <JourneyHead head={data.journey_head} variant="card" />
          )}

          {/* ============== THE LAB THAT IS HAPPENING ============== */}
          {/* ★ Moved ABOVE the season-long cards, and it is not a decorative choice. Everything
              between here and the calendar — the competition, the leagues, the polls, the pulses
              — is a to-do list that spans months. This is the next hour. A member opening their
              phone while standing up should not scroll past four cards about the season to find
              the room they are trying to get into.

              No date test in here: the server sends `live_lab` only inside the Lab's own window
              (routes/bravos._lab_in_progress — from a day before the doors open until three days
              after they shut) and null on all the other days, and it carries the `phase` that
              decides which half of the card draws. `pre_surveys` only ever holds what THIS
              reader has not answered yet.
              ★ The id is the LAB's, never the session event's: a Bravos session sits on the
              Season, and a code minted against the Season is refused by the mint anyway —
              nobody attends a container. */}
          {data && data.live_lab && (
            <LabToday
              lab={data.live_lab}
              /* The calendar already carries this Lab's day, hours, zone and room, so the card
                 is complete before its own request lands. Matched case-insensitively: both ids
                 come from the same row today, but SQL Server hands GUIDs back upper-cased and
                 half this codebase lower-cases them on the way through. */
              calendarRow={(data.calendar || []).find(
                (r) => String(r.id).toLowerCase() === String(data.live_lab.id).toLowerCase())}
              preSurveys={data.pre_surveys || []}
              postSurveys={data.post_surveys || []}
              onShowMeetQr={() => setShowMeetQr(true)}
            />
          )}
          {showMeetQr && data && data.live_lab && (
            <MeetQrSheet eventId={data.live_lab.id} onClose={() => setShowMeetQr(false)} />
          )}

          {/* "You're in group 3" — directly under the Lab it belongs to, because during the hour
              it exists it is the most urgent thing on this screen and it is read while somebody
              is standing up. Deliberately NOT folded into LabToday: it has its own payload and
              its own lifecycle (a host publishes it mid-event). Null on the other 364 days. */}
          {data && data.breakout && (
            <Card><MyBreakout breakout={data.breakout} /></Card>
          )}

          {/* The brand's WhatsApp community — the same full-width row the participant home
              (Home.js) gives Pink Ladies Games. The link is the one on Machina › event › Social
              Media, which `social_links` already carries; the follow-us strip further down leaves
              WhatsApp out because a group is joined, not followed. */}
          {data && data.social_links && data.social_links.whatsapp && (
            <a className="bravos-whatsapp" href={data.social_links.whatsapp}
               target="_blank" rel="noopener noreferrer">
              <Icon name="whatsapp" pack="brands" />
              <span>{t('joinCommunityWhatsapp')}</span>
            </a>
          )}

          {/* ★ Their EMPLOYER'S questions, with no evening behind them — a poll. Everything else
              on this home is an event or an instrument we wrote; this is the one card the company
              itself fills, which is why it sits above the personal pulses: somebody is waiting on
              the answer. The server only sends a set that is flagged for the home, published and
              inside its window, so a card here is always answerable — and answering happens on
              the set's own published page (/f/<slug>), in-app, no second renderer. */}
          {/* ★ How their company's competition stands — their house, the board, their teams.
              Above the leagues card because the two answer different questions and this is the
              one people ask first: the leagues card says what they are IN this season, this says
              how it is GOING. Self-fetching (the house standing is expensive) and it draws
              nothing at all for a company that runs no competition, so every other member's home
              is unchanged. */}
          {data && view === 'member' && data.is_engagement && <MyCompetition />}

          {/* ★ The leagues they can join or follow. A league is the only thing on this home that
              runs ACROSS evenings, so it sits with the polls rather than in the calendar: the
              calendar answers "what is on Thursday" and this answers "what am I in this season".
              Pressing it opens the league's own event page, where the RSVP buttons already
              live — no second sign-up flow, no second answer to disagree with the first. */}
          {data && view === 'member' && myLeagues.length > 0 && (
            <Card>
              <Title my="1" className="mt-2">
                <Icon name="leaderboard" theme="primary" className="me-2" />
                {t('leagues')}
              </Title>
              <p className="bravos-q__hint mb-2">{t('myLeaguesNote')}</p>
              {myLeagues.map((lg) => (
                <button
                  key={lg.id}
                  type="button"
                  className="bravos-cta"
                  onClick={() => navigate(`/public-event/${lg.master_event_id}`)}
                >
                  <span className="bravos-cta__name">{lg.name}</span>
                  <span className="bravos-cta__tag bravos-cta__tag--do">
                    {lg.is_closed ? t('leagueFinished') : t('leagueJoin')}
                  </span>
                  <Icon name="angle-right" className="bravos-event__chev" />
                </button>
              ))}
            </Card>
          )}

          {data && view === 'member' && (data.polls || []).length > 0 && (
            <Card>
              <Title my="1" className="mt-2">
                <Icon name="comment-question" theme="primary" className="me-2" />
                {t('askedOfYou')}
              </Title>
              <p className="bravos-q__hint mb-2">{t('askedOfYouNote')}</p>
              {data.polls.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="bravos-cta"
                  onClick={() => navigate(`/f/${p.slug}`)}
                >
                  <span className="bravos-cta__name">{p.name}</span>
                  {/* Answered polls stay on the card rather than disappearing off it: the point
                      of the pill is that they can SEE they are done, and still walk back in and
                      change what they said. */}
                  <span className={`bravos-cta__tag bravos-cta__tag--${p.answered ? 'done' : 'do'}`}>
                    {p.answered ? t('changeMyAnswers') : t('answerIt')}
                  </span>
                  <Icon name="angle-right" className="bravos-event__chev" />
                </button>
              ))}
            </Card>
          )}

          {data && view === 'member' && data.ctas && data.ctas.length > 0 && (
            <Card>
              <Title my="1" className="mt-2">
                <Icon name="list-check" theme="primary" className="me-2" />
                {t('yourPulses')}
              </Title>
              <p className="bravos-q__hint mb-2">{t('yourPulsesNote')}</p>
              {data.ctas.map((c) => (
                <div key={c.key} className="bravos-cta-row">
                  <button
                    type="button"
                    className="bravos-cta"
                    onClick={() => navigate(`/bravos/survey/${c.key}`)}
                  >
                    <span className="bravos-cta__name">{c.name}</span>
                    {!c.available ? (
                      <span className="bravos-cta__tag">{t('comingSoon')}</span>
                    ) : c.completed ? (
                      <span className="bravos-cta__tag bravos-cta__tag--done">{t('seeResults')}</span>
                    ) : (
                      <span className="bravos-cta__tag bravos-cta__tag--do">{t('doTest')}</span>
                    )}
                    <Icon name="angle-right" className="bravos-event__chev" />
                  </button>
                  {/* Share the pulse's PUBLIC twin (a lead magnet): copies a warm invite + the
                      public link, ready to drop into WhatsApp/email so more of the market takes it. */}
                  {c.share_link && (
                    <button
                      type="button"
                      className="bravos-copy bravos-cta__share"
                      title={t('sendTestHint')}
                      onClick={() => copyLink(`pulse-${c.key}`, c.share_link,
                        t('pulseShareText', { name: c.name }))}
                    >
                      <Icon name={copied === `pulse-${c.key}` ? 'check' : 'paper-plane'} />
                      {copied === `pulse-${c.key}` ? t('copied') : t('sendTest')}
                    </button>
                  )}
                </div>
              ))}
            </Card>
          )}

          {/* Only the PITCH stays here, above the calendar — that is where the decision to
              buy in happens. A member who already owns the membership gets that card further
              down, next to the wallet it fills (see below). */}
          {data && view === 'member' && data.membership_offer
            && data.membership_offer.state !== 'owned' && (
            <MembershipOffer
              offer={data.membership_offer}
              onJoin={() => navigate('/join')}
              /* Router state, not a query param: usePersistUrlParams strips query params on
                 arrival, so ?tab=company would never reach the page. */
              onJoinCompany={() => navigate('/join', { state: { tab: 'company' } })}
            />
          )}

          <Card>
            <Title my="1" className="mt-2">
              <Icon name="calendar" theme="primary" className="me-2" />
              {t('eventCalendar')}
            </Title>

            {claimNotice && <p className="bravos-cal__notice">{claimNotice}</p>}

            {/* ★ One calendar, not two. Our own company's events are IN the list below, so the
                company-only view is a button in this card — a second card called "Our events"
                next to a card called "Event calendar" read as two rival calendars. */}
            {data && view === 'member' && data.is_engagement && (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => navigate('/company-events')}
              >
                <Icon name="calendar-star" /> {t('ourEvents')} <Icon name="angle-right" />
              </button>
            )}
            {data && view === 'member' && data.is_engagement && (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => navigate('/clubs')}
              >
                <Icon name="users" /> {t('clubsTitle')} <Icon name="angle-right" />
              </button>
            )}

            {/* Inviting people to a Lab is its own screen now — the rows below just open the
                Lab. One entry point beats a copy-link button on every row. */}
            {view === 'contact' && data && data.can_invite && (
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => navigate('/invite-to-lab')}
              >
                <Icon name="user-add" /> {t('inviteToLab')}
              </button>
            )}

            {upcoming.length > 0 && (
              <>
                <p className="bravos-cal__label">{t('upcomingEvents')}</p>
                {upcoming.slice(0, CALENDAR_ROWS).map((e) => (
                  <EventRow
                    key={e.id}
                    event={e}
                    view={view}
                    isEcosystem={!!(data && data.is_ecosystem)}
                    claiming={claiming}
                    canceling={canceling}
                    rsvping={rsvping}
                    copied={copied}
                    onOpen={() => (e.manage ? manageEvent(e.id) : openEvent(e.id))}
                    onClaim={claimSeat}
                    onBook={bookLab}
                    onRsvp={answerRsvp}
                    onCopyFriend={copyFriend}
                    onCancel={cancelBooking}
                    onInviteNetwork={inviteNetwork}
                  />
                ))}
                {upcoming.length > CALENDAR_ROWS && (
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={() => navigate('/member-home/upcoming')}
                  >
                    {t('showMore')} <Icon name="angle-right" />
                  </button>
                )}
              </>
            )}

            {past.length > 0 && (
              <>
                <p className="bravos-cal__label bravos-cal__label--past">
                  {t('pastEvents')}
                </p>
                {past.slice(0, CALENDAR_ROWS).map((e) => (
                  <EventRow
                    key={e.id}
                    event={e}
                    view={view}
                    claiming={claiming}
                    canceling={canceling}
                    rsvping={rsvping}
                    copied={copied}
                    onOpen={() => (e.manage ? manageEvent(e.id) : openEvent(e.id))}
                    onClaim={claimSeat}
                    onBook={bookLab}
                    onRsvp={answerRsvp}
                    onCopyFriend={copyFriend}
                    onCancel={cancelBooking}
                  />
                ))}
                {past.length > CALENDAR_ROWS && (
                  <button
                    type="button"
                    className="bravos-submit bravos-submit--ghost"
                    onClick={() => navigate('/member-home/past')}
                  >
                    {t('showMore')} <Icon name="angle-right" />
                  </button>
                )}
              </>
            )}

            {calendar.length === 0 && (
              <p className="text-muted mb-0">{t('noUpcomingEvents')}</p>
            )}
          </Card>

          {/* Blog / content feed — directly under the calendar (5 items + "Show more"). The
              admin also gets the way in to the company's own feeds from here, next to the
              articles those feeds produce. */}
          {data && (
            <ContentFeed
              items={data.content_feed}
              more={data.content_feed_more}
              onMore={() => navigate('/blogs')}
              onSubmitArticle={data.can_submit_blog
                ? () => navigate('/edit-specialist-profile#submit-article') : undefined}
              onManage={view === 'contact' && !offBravos ? () => navigate('/company-feeds') : undefined}
              manageLocked={view === 'contact' && offBravos}
            />
          )}

          {/* Missions — what there is to go and DO, and what is waiting for a yes.

              ★ The card draws while there is ANYTHING to see, not only while something is left
              to do. It used to vanish the moment the last mission was answered, which took away
              the only way back to the answers still waiting for approval — and a mission answer
              is the one thing on this platform that is invisible until somebody approves it.
              The sub-line is what changes; when there is nothing left to do it opens straight
              on the answers instead of on an empty "Open" list.

              ★ Not on the admin home of an engagement company: the tile grid up the page already
              carries the missions, with the same open count and the same waiting queue. Two ways
              in to one screen, a page apart, is clutter — not a second chance. */}
          {data && data.missions_total > 0
            && !(view === 'contact' && data.is_engagement) && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate(missionsTo())}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="flag" theme="primary" className="me-2" />
                    {t('missionsTitle')}
                  </span>
                  <span className="bravos-memberslink__count">
                    {data.missions_todo > 0
                      ? t('missionsTodo', { n: data.missions_todo })
                      : (data.missions_waiting > 0
                        ? t('missionsWaiting', { n: data.missions_waiting })
                        : t('missionsAllDone'))}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
              {/* ★ And a few of the answers that were said yes to. The same strip the feed and
                  the wall carry, and the strongest of the three: a mission picture was
                  COMMISSIONED — somebody was asked to go and take it — so a row of them is the
                  clearest possible answer to "what would I even do here". Shuffled, colleagues'
                  as much as your own (an approved answer is published to the whole company),
                  and only ever APPROVED ones: an answer is invisible until somebody says yes,
                  and a home page must not be the place that leaks it.

                  The server sends none in the admin view — the tile grid up the page carries
                  the missions there — and none while nothing has been approved, which is the
                  same "no pictures, exactly the line it was" the other two fall back to. */}
              <PhotoStrip items={data.missions_teaser} label={t('missionsTitle')}
                          onOpen={() => navigate(missionsTo())} />
            </Card>
          )}

          {/* The activity stream, for the MEMBER home: a member's feed is the Labs they took
              part in, and an employee of a company that bought engagement gets their company's
              cards on top of that. The sub-line counts the last WEEK — a running total is
              trivia, "9 this week" is a reason to open it.

              ★ The admin reads the same number off a tile in the grid instead. This card and the
              wall below it were the two cheapest things on the page to look at, and they sat
              furthest down it; a member has no grid to move them into, so here they stay. */}
          {data && view === 'member' && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/company-feed')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="megaphone" theme="primary" className="me-2" />
                    {t('feedTitle')}
                  </span>
                  <span className="bravos-memberslink__count">
                    {data.feed_recent > 0
                      ? t('feedThisWeek', { n: data.feed_recent })
                      : t('feedNote')}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
              {/* ★ The last few pictures on the stream, the same strip the wall card carries and
                  the same reason for it: "9 this week" is a fact and a face is an invitation.
                  These are the NEWEST, where the wall's are a shuffle — and the server hands the
                  wall a list of what landed here so one picture never draws twice on one screen.
                  Decoration in the strict sense: no teaser, no strip, and the card is exactly
                  the line it was. */}
              <PhotoStrip items={data.feed_teaser} label={t('feedTitle')}
                          onOpen={() => navigate('/company-feed')} />
            </Card>
          )}

          {/* The photo wall — every picture from every event this person may see, at once (F8).
              Member home only, beside the feed, and only when there ARE pictures: a way in
              promising a wall and opening an empty room is worse than none. The count is a
              TOTAL, where the feed's is a week — a wall is a collection, and "312 photos" is
              the reason to open it.

              ★ And a few of those pictures ride along, reshuffled on every load, because a row
              of faces is a reason to tap and the sentence "312 photos" is only a fact. The
              strip is decoration in the strict sense: if the server sent none — an older
              payload, a slow read, a wall of pictures that lost their blobs — the card falls
              back to exactly what it was and nothing here breaks. */}
          {data && view === 'member' && data.photo_count > 0 && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/photo-wall')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="camera" theme="primary" className="me-2" />
                    {t('photoWallTitle')}
                  </span>
                  <span className="bravos-memberslink__count">
                    {t('photoWallCount', { n: data.photo_count })}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
              <PhotoStrip items={data.photo_teaser} label={t('photoWallTitle')}
                          onOpen={() => navigate('/photo-wall')} />
            </Card>
          )}

          {/* The people they work with, with their faces on. Its own way in rather than a chip
              somebody has to find, because the card above it only draws when there ARE pictures —
              so on a company that has never uploaded one, the wall of colleagues would have been
              unreachable for everybody except the admin. Which is exactly the week it is most
              worth having.

              ★ Faces but no COUNT. A few colleagues, reshuffled on every load, for exactly the
              reason the two cards above carry a strip: a row of faces is a reason to tap and a
              sentence is only a fact. The number is not there because counting the roster is four
              queries and ~0.7s where the faces are one, and it is free on the screen behind the
              card. The strip is decoration in the strict sense — no faces and the card is exactly
              the line it was.

              ★ `has_tribe`, not `is_engagement`: an individual member has people around them too,
              and they are the business unit's own community — everybody they meet at the Labs.
              The card, the wall and the game behind it are the same three for both; only the
              roster differs, and that is decided server-side (`_bravos_scope_gate`). */}
          {data && view === 'member' && data.has_tribe && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/photo-wall?source=people')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="users" theme="primary" className="me-2" />
                    {t('communityTitle')}
                  </span>
                  {/* ★ An invitation, not a description, for somebody who is not on the wall.
                      "Everyone in our tribe, with their faces on" is a promise the screen cannot
                      keep for a person it does not list — and the empty face strip beside it
                      would read as a bug rather than as a question. */}
                  <span className="bravos-memberslink__count">
                    {data.community_join ? t('communityJoinNote') : t('communityMemberNote')}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
              <PhotoStrip items={data.community_teaser} label={t('communityTitle')}
                          onOpen={() => navigate('/photo-wall?source=people')} />
            </Card>
          )}

          {/* ★ Do you know your tribe? — its own line rather than a second button on the card
              above, because it asks for something different: that card is "go and look at the
              people here", this one is "find out what you do not know about them". It now leads
              to the game's OWN page rather than to the wall with `?quiz=1` on it — a card that
              lands somebody on a screen where they still have to find the thing they tapped for
              is a card that gets tapped once, and a query param is stripped on arrival anyway.

              No strip and no score: the number of rounds somebody has played is not a reason to
              play another, and a personal total is exactly what this feature does not show.

              ★ Same gate as the card above, and for the same reason: a member with no company
              plays it over the business unit's community. */}
          {/* ★ Recognition (F7) sits directly under the tribe card, because it is the same set of
              people asked for something different: that card is "go and look at them", this one is
              "say what one of them did". A cheer is the only thing on this home that somebody else
              decides about you, which is why the card leads with what is WAITING when anything is
              — that number is a decision only this person can make, and it is the whole reason a
              pending cheer exists. At zero it is an invitation instead, never a count of nothing. */}
          {data && view === 'member' && data.is_engagement && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate(data.cheers_waiting > 0
                        ? '/cheers-for-me' : '/cheer')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="hands-clapping" theme="primary" className="me-2" />
                    {data.cheers_waiting > 0 ? t('cheerWaitingTitle') : t('cheerTitle')}
                  </span>
                  <span className="bravos-memberslink__count">
                    {data.cheers_waiting > 0
                      ? t('cheerWaitingCount', { count: data.cheers_waiting })
                      : t('cheerHomeNote')}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {data && view === 'member' && data.has_tribe && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/know-your-tribe')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="zoom-in" theme="primary" className="me-2" />
                    {t('quizTitle')}
                  </span>
                  <span className="bravos-memberslink__count">{t('quizHomeNote')}</span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {/* Hosting is not a tier. An ecosystem member running a Lab and an employee running
              their own company's townhall are doing the same job, and until now only the first
              had a way in — the host screen was reachable from the event EDITOR, which a host
              who is not an admin cannot open. So the card sits on every Bravos home (admin,
              member, ecosystem) and shows itself the moment there is something to run. */}
          {offBravos && view === 'contact' && (
            <LockedRow icon="microphone" title={t('eventsImHosting')} />
          )}
          {data && !offBravos && data.hosting_count > 0 && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/my-lab')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="microphone" theme="primary" className="me-2" />
                    {t('eventsImHosting')}
                  </span>
                  <span className="bravos-memberslink__count">
                    {t('eventsHostedCount', { n: data.hosting_count || 0 })}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {/* The wallet row — and, for a member who bought in, the membership itself: one
              card, one chevron, straight to /wallet. */}
          {data && view === 'member' && (
            <WalletTickets
              membership={data.membership_offer
                && data.membership_offer.state === 'owned' ? data.membership_offer : null}
            />
          )}

          {/* ★ NOT ecosystem-gated, unlike the cards below it. My Network's first tab is the
              people you met by QR at an event, which every member collects — so an ordinary
              member reaches the page too and simply gets one tab. The subtitle follows: a
              connection count is an ecosystem number and would read as "0 people" to somebody
              who has in fact met plenty. */}
          {data && view === 'member' && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/my-network')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="users" theme="primary" className="me-2" />
                    {t('myNetwork')}
                  </span>
                  <span className="bravos-memberslink__count">
                    {data.is_ecosystem
                      ? t('networkCount', { n: data.network_count || 0 })
                      : t('meetTabMet')}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {/* Ecosystem tier (a member holding a community role) — extra capabilities. */}
          {data && data.is_ecosystem && view === 'member' && data.has_vouchers && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/my-vouchers')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="ticket" theme="primary" className="me-2" />
                    {t('myVouchers')}
                  </span>
                  {/* Counted in redemptions, not codes: one code with five uses is five
                      invitations. An uncapped code has no free number, so it says so. */}
                  <span className="bravos-memberslink__count">
                    {data.voucher_counts ? (
                      data.voucher_counts.unlimited
                        ? t('myVouchersUnlimitedUsed', { used: data.voucher_counts.used })
                        : t('myVouchersFreeUsed', { free: data.voucher_counts.free,
                                                    used: data.voucher_counts.used })
                    ) : t('myVouchersHint')}
                  </span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {/* Specialists directory — open to EVERY member and the contact person (both views),
              not just ecosystem members. Being listed stays ecosystem + approval. */}
          {offBravos && view === 'contact' && (
            <LockedRow icon="briefcase" title={t('specialistsDirectory')} hint={t('specialistsHint')} />
          )}
          {data && !offBravos && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/specialists')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="briefcase" theme="primary" className="me-2" />
                    {t('specialistsDirectory')}
                  </span>
                  <span className="bravos-memberslink__count">{t('specialistsHint')}</span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {data && data.is_ecosystem && view === 'member' && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/my-claims')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="diploma" theme="primary" className="me-2" />
                    {t('myClaims')}
                  </span>
                  <span className="bravos-memberslink__count">{t('myClaimsHint')}</span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {data && data.is_ecosystem && view === 'member' && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/my-cases')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="balance-scale-right" theme="primary" className="me-2" />
                    {t('myCases')}
                  </span>
                  <span className="bravos-memberslink__count">{t('myCasesHint')}</span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {/* Share your member badge — same audience and the same outbound intent as
              Refer a friend below it, so the two sit together. */}
          {offBravos && view === 'contact' && (
            <LockedRow icon="id-badge" title={t('bravosBadge')} hint={t('bravosBadgeHint')} />
          )}
          {data && !offBravos && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/member-badge')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="id-badge" theme="primary" className="me-2" />
                    {t('bravosBadge')}
                  </span>
                  <span className="bravos-memberslink__count">{t('bravosBadgeHint')}</span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {/* Refer a friend — available to every member (both views), near the bottom. */}
          {offBravos && view === 'contact' && (
            <LockedRow icon="user-add" title={t('referFriend')} hint={t('referFriendCtaSub')} />
          )}
          {data && !offBravos && (
            <Card>
              <button type="button" className="bravos-memberslink"
                      onClick={() => navigate('/refer')}>
                <span className="bravos-memberslink__body">
                  <span className="bravos-memberslink__title">
                    <Icon name="user-add" theme="primary" className="me-2" />
                    {t('referFriend')}
                  </span>
                  <span className="bravos-memberslink__count">{t('referFriendCtaSub')}</span>
                </span>
                <Icon name="angle-right" className="bravos-event__chev" />
              </button>
            </Card>
          )}

          {/* The community's own numbers, down with the social block: the views this card
              counts are the reach of the very pages "Follow Bravos" links to. */}
          {data && data.is_ecosystem && view === 'member' && <CommunityStats />}

          {/* The BU's public social pages — their own badge card, down at the bottom
              just above the sponsors (icons entered on Machina › event › Social Media). */}
          {data && data.social_links
            && SOCIAL_ORDER.some((k) => data.social_links[k]) && (
            <Card>
              <div className="bravos-socialcard">
                <span className="bravos-socialcard__label">{offBravos && brand && brand.name ? t('followBrand', { brand: brand.name }) : t('followUs')}</span>
                <SocialLinks links={data.social_links} />
              </div>
            </Card>
          )}

          {/* ============== SPONSORS (bottom of every Bravos home) ============== */}
          {data && data.sponsors && data.sponsors.length > 0 && (
            <Card>
              <div className="bravos-sponsors">
                <div className="bravos-sponsors__label">{t('sponsoredBy')}</div>
                {/* One column per sponsor type, most important first (the API
                    orders them), each cut off from the next by a rule; untyped
                    sponsors trail as a last, unlabelled column. The wall clips
                    the row's overhanging left edge so the rule vanishes on the
                    first column of every wrapped line -- see the stylesheet. */}
                <div className="bravos-sponsors__wall">
                  <div className="bravos-sponsors__row">
                    {groupSponsorsByType(data.sponsors).map((group) => (
                      <div className="bravos-sponsors__group" key={group.type || '_untyped'}>
                        {group.type && (
                          <div className="bravos-sponsors__type">{group.type}</div>
                        )}
                        <div className="bravos-sponsors__grid">
                          {group.items.map((s) => (
                            // A sponsor with its own URL links out; the rest open the
                            // internal public-company page. The key carries the type too:
                            // a sponsor tagged with several types appears in each group.
                            s.url ? (
                              <a
                                key={`${group.type || ''}-${s.id}`}
                                href={s.url}
                                target="_blank"
                                rel="noreferrer"
                                className="bravos-sponsors__item"
                                style={{ backgroundImage: `url(${s.photo_url || fallbackCompany})` }}
                                aria-label={s.name || ''}
                              />
                            ) : (
                              <span
                                key={`${group.type || ''}-${s.id}`}
                                role="button"
                                tabIndex={0}
                                className="bravos-sponsors__item"
                                onClick={() => navigate(`/public-company/${s.id}`)}
                                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/public-company/${s.id}`); }}
                                style={{ backgroundImage: `url(${s.photo_url || fallbackCompany})` }}
                                aria-label={s.name || ''}
                              />
                            )
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </Base>
  );
};

export default MemberHome;
